<script setup lang="ts">
/**
 * AddressCommsFields — shared address-channel editor (PODZ-1814).
 *
 * Renders the email + phone row editors revealed when an address's "Receives
 * Additional Communications" box is checked. Consumed by BOTH the Profile
 * AddressEditModal and the Schedule wizard WizardStepAddress so the two surfaces
 * never diverge (D-Shared). Each consuming surface owns its OWN checkbox; this
 * component renders ONLY the channel sections.
 *
 * Row mechanics (add/remove/mask/EMAIL_REGEX) are copied from
 * ContactEditModal.vue, but every `primary` / setPrimary* concept is DROPPED —
 * address channels carry NO primary flag (D-Layout); the "Primary" heading is
 * purely presentational (the first array entry). The phone-type control is a
 * native <select> with mobile/home/work/other (D-PhoneType), matching
 * ContactEditModal's native-select idiom (NOT BaseSelect).
 *
 * v-model:emails / v-model:phones — props are immutable, so every mutation emits
 * the COMPLETE updated array; the parent stays authoritative.
 */
import { computed } from 'vue';
import {
  formatPhone,
  isValidEmail,
  isValidPhone,
  type AddressEmailPayload,
  type AddressPhonePayload,
} from '@sera/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPlus, faTrashCan } from '@fortawesome/pro-solid-svg-icons';

// Working-row shapes carry an optional client-only `_key` so v-for can key on a
// stable identity (WR-04). New rows get a uid; existing rows already have `id`.
// `_key` is NEVER part of the AddressEmailPayload/AddressPhonePayload payload
// contract — the consuming views build the API payload from email_address /
// phone_number / type / label only, so the key cannot leak to the wire.
type EmailRow = AddressEmailPayload & { _key?: number };
type PhoneRow = AddressPhonePayload & { _key?: number };

interface Props {
  emails: EmailRow[];
  phones: PhoneRow[];
}
const props = defineProps<Props>();

const emit = defineEmits<{
  'update:emails': [value: EmailRow[]];
  'update:phones': [value: PhoneRow[]];
}>();

const PHONE_TYPES = ['mobile', 'home', 'work', 'other'];

// Monotonic client-side uid for new rows (WR-04). Module-scoped is fine — it
// only needs to be unique within a single editor instance's lifetime. The
// v-for keys namespace server ids (`id-N`) separately from these client uids
// (`k-N`), so a server id can never collide with a `_key` even though the
// counter is never reset (REVIEW-2 WR-03).
let uidSeq = 0;
function nextKey(): number {
  uidSeq += 1;
  return uidSeq;
}

// ── Emails ──────────────────────────────────────────────────────────────────
function emitEmails(next: EmailRow[]): void {
  emit('update:emails', next);
}
function addEmail(): void {
  emitEmails([
    ...props.emails,
    { email_address: '', label: '', _key: nextKey() },
  ]);
}
function removeEmail(index: number): void {
  emitEmails(props.emails.filter((_, i) => i !== index));
}
function setEmailAddress(index: number, value: string): void {
  emitEmails(
    props.emails.map((e, i) =>
      i === index ? { ...e, email_address: value } : e,
    ),
  );
}

// ── Phones ──────────────────────────────────────────────────────────────────
function emitPhones(next: PhoneRow[]): void {
  emit('update:phones', next);
}
function addPhone(): void {
  emitPhones([
    ...props.phones,
    { phone_number: '', type: 'mobile', label: '', _key: nextKey() },
  ]);
}
function removePhone(index: number): void {
  emitPhones(props.phones.filter((_, i) => i !== index));
}
// Live (xxx) xxx-xxxx mask applied as the customer types (same as ContactEditModal).
// Trim surrounding whitespace on output so the payload value matches the email
// trim behavior (WR-03); formatPhone never emits leading/trailing spaces, but
// the explicit trim keeps the two channels symmetric and guards future edits.
function setPhoneNumber(index: number, value: string): void {
  emitPhones(
    props.phones.map((p, i) =>
      i === index ? { ...p, phone_number: formatPhone(value).trim() } : p,
    ),
  );
}
function setPhoneType(index: number, value: string): void {
  emitPhones(
    props.phones.map((p, i) => (i === index ? { ...p, type: value } : p)),
  );
}

// ── Validation (single source of truth, WR-01 + WR-02) ───────────────────────
// Empty rows are ignored here (they are filtered out before save by the views).
// A non-empty email must pass isValidEmail; a non-empty phone must pass
// isValidPhone (exactly 10 digits). Both rules live in @sera/core so this
// editor — and the views that consult it via validate()/invalid — never drift
// from one another (IN-04).
const hasInvalidEmail = computed(() =>
  props.emails.some((e) => {
    const value = e.email_address.trim();
    return value !== '' && !isValidEmail(value);
  }),
);
const hasInvalidPhone = computed(() =>
  props.phones.some((p) => {
    const value = p.phone_number.trim();
    return value !== '' && !isValidPhone(value);
  }),
);
const invalid = computed(() => hasInvalidEmail.value || hasInvalidPhone.value);

/**
 * Returns an inline error message when any non-empty channel row is invalid,
 * else null. Views call this in their save gate and surface the string via
 * their existing error block, keeping the modal open.
 */
function validate(): string | null {
  if (hasInvalidEmail.value) return 'Please enter a valid email address.';
  if (hasInvalidPhone.value)
    return 'Please enter a valid 10-digit phone number.';
  return null;
}

defineExpose({ invalid, validate });
</script>

<template>
  <div data-testid="address-comm-fields">
    <!-- Emails -->
    <fieldset class="mb-4">
      <div class="mb-2 flex items-center justify-between">
        <legend
          class="text-2xs font-semibold uppercase tracking-wider text-muted"
        >
          Email addresses
        </legend>
        <button
          type="button"
          data-testid="address-comm-add-email"
          class="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline focus:outline-none"
          @click="addEmail"
        >
          <FontAwesomeIcon :icon="faPlus" class="size-3" />
          Add email
        </button>
      </div>
      <div
        v-for="(email, i) in emails"
        :key="
          email.id != null
            ? `id-${email.id}`
            : email._key != null
              ? `k-${email._key}`
              : `email-${i}`
        "
        :data-testid="`address-comm-email-row-${i}`"
        class="mb-2 grid grid-cols-[1fr_auto] items-center gap-2"
      >
        <input
          :value="email.email_address"
          data-testid="address-comm-email-input"
          type="email"
          autocomplete="email"
          placeholder="name@example.com"
          class="w-full min-w-0 rounded-md border border-border bg-field px-3 py-2.5 text-sm text-foreground placeholder:text-muted transition-colors focus:border-primary focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-primary/45"
          @input="setEmailAddress(i, ($event.target as HTMLInputElement).value)"
        />
        <button
          type="button"
          :data-testid="`address-comm-email-remove-${i}`"
          aria-label="Remove email"
          class="flex size-8 items-center justify-center rounded-sm text-subtle transition-colors hover:bg-danger/10 hover:text-danger focus:outline-none"
          @click="removeEmail(i)"
        >
          <FontAwesomeIcon :icon="faTrashCan" class="size-4" />
        </button>
      </div>
    </fieldset>

    <!-- Phones -->
    <fieldset class="mb-1">
      <div class="mb-2 flex items-center justify-between">
        <legend
          class="text-2xs font-semibold uppercase tracking-wider text-muted"
        >
          Phone numbers
        </legend>
        <button
          type="button"
          data-testid="address-comm-add-phone"
          class="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline focus:outline-none"
          @click="addPhone"
        >
          <FontAwesomeIcon :icon="faPlus" class="size-3" />
          Add phone
        </button>
      </div>
      <div
        v-for="(phone, i) in phones"
        :key="
          phone.id != null
            ? `id-${phone.id}`
            : phone._key != null
              ? `k-${phone._key}`
              : `phone-${i}`
        "
        :data-testid="`address-comm-phone-row-${i}`"
        class="mb-2 grid grid-cols-[1fr_auto_auto] items-center gap-2"
      >
        <input
          :value="phone.phone_number"
          data-testid="address-comm-phone-input"
          type="tel"
          autocomplete="tel"
          placeholder="(555) 123-4567"
          class="w-full min-w-0 rounded-md border border-border bg-field px-3 py-2.5 text-sm text-foreground placeholder:text-muted transition-colors focus:border-primary focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-primary/45"
          @input="setPhoneNumber(i, ($event.target as HTMLInputElement).value)"
        />
        <select
          :value="phone.type"
          :data-testid="`address-comm-phone-type-${i}`"
          class="w-24 shrink-0 rounded-md border border-border bg-field px-2 py-2.5 text-sm text-foreground transition-colors focus:border-primary focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-primary/45"
          @change="setPhoneType(i, ($event.target as HTMLSelectElement).value)"
        >
          <option v-for="t in PHONE_TYPES" :key="t" :value="t">
            {{ t }}
          </option>
        </select>
        <button
          type="button"
          :data-testid="`address-comm-phone-remove-${i}`"
          aria-label="Remove phone"
          class="flex size-8 items-center justify-center rounded-sm text-subtle transition-colors hover:bg-danger/10 hover:text-danger focus:outline-none"
          @click="removePhone(i)"
        >
          <FontAwesomeIcon :icon="faTrashCan" class="size-4" />
        </button>
      </div>
    </fieldset>
  </div>
</template>
